import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Clock3,
  Link2,
  Plus,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  BlumeBmpProduct,
  BlumeMapping,
  BlumeMappingsResponse,
  BlumeProductMappingsResponse,
  BlumeQuickBooksItem,
  BmpCustomerSummary,
  createAndMapBlumeProduct,
  disconnectBlume,
  getBlumeMappings,
  getBlumeProductMappings,
  ignoreBlumeCustomer,
  mapBlumeCustomer,
  mapExistingBlumeProduct,
  syncBlumeCustomers,
  unignoreBlumeCustomer,
  unmapBlumeCustomer,
  unmapBlumeProduct,
} from '../../features/integrations/blume/api';
import './BlumeIntegrationPage.scss';
import { AppSidebar } from '../../components/AppSidebar/AppSidebar';
import { BlumeInvoiceSettingsPanel } from '../../components/BlumeInvoiceSettingsPanel/BlumeInvoiceSettingsPanel';

type IntegrationView = 'customers' | 'products' | 'invoice-settings';

type FilterType = 'all' | 'mapped' | 'review' | 'blume-only' | 'bmp-only' | 'ignored' | 'stale';

type MappingModalState = {
  mapping: BlumeMapping;
} | null;

function formatCurrency(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return '—';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatDate(value: string | null) {
  if (!value) {
    return 'Never';
  }

  return new Date(value).toLocaleString();
}

function getApiErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data as
      | {
          message?: string;
          error?: string;
        }
      | undefined;

    if (typeof responseData?.message === 'string') {
      return responseData.message;
    }

    if (typeof responseData?.error === 'string') {
      return responseData.error;
    }

    return error.message || fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
}

function getMappingLabel(mapping: BlumeMapping) {
  if (!mapping.isPresentInBlume) {
    return 'Stale';
  }

  switch (mapping.mappingStatus) {
    case 'AUTO_MATCHED':
      return 'Auto matched';

    case 'MANUAL':
      return 'Manual';

    case 'IGNORED':
      return 'Ignored';

    default:
      return mapping.suggestedBmpCustomer ? 'Needs review' : 'Blume only';
  }
}

function getMappingTone(mapping: BlumeMapping) {
  if (!mapping.isPresentInBlume) {
    return 'muted';
  }

  switch (mapping.mappingStatus) {
    case 'AUTO_MATCHED':
    case 'MANUAL':
      return 'success';

    case 'IGNORED':
      return 'muted';

    default:
      return mapping.suggestedBmpCustomer ? 'warning' : 'neutral';
  }
}

export function BlumeIntegrationPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoSyncTriggeredRef = useRef(false);
  const queryClient = useQueryClient();

  const [activeView, setActiveView] = useState<IntegrationView>('customers');
  const [filter, setFilter] = useState<FilterType>('all');
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState<MappingModalState>(null);
  const [isAutoSyncing, setIsAutoSyncing] = useState(false);
  const [autoSyncError, setAutoSyncError] = useState<unknown>(null);

  const { data, isLoading, error } = useQuery<BlumeMappingsResponse, Error>({
    queryKey: ['blume-mappings'],
    queryFn: getBlumeMappings,
  });

  const refreshData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: ['blume-mappings'],
      }),
      queryClient.invalidateQueries({
        queryKey: ['blume-overview'],
      }),
    ]);
  };

  const refreshCustomersMutation = useMutation({
    mutationFn: syncBlumeCustomers,
    onSuccess: () => {
      void refreshData();
    },
  });

  /*
   * Header-level "Sync all":
   *
   * 1. Sync Blume customers / credit first.
   * 2. Refresh the live QuickBooks product catalog second.
   *
   * getBlumeProductMappings() now refreshes QBO through Blume before
   * returning the product-mapping dataset, so this is the product-side
   * sync operation we want here.
   */
  const syncAllMutation = useMutation({
    mutationFn: async () => {
      const customerSync = await syncBlumeCustomers();
      const productMappings = await getBlumeProductMappings();

      return {
        customerSync,
        productMappings,
      };
    },

    onSuccess: async ({ productMappings }) => {
      /*
       * We already made the product API call above, so put that exact
       * response into React Query instead of immediately calling it again.
       */
      queryClient.setQueryData(['blume-product-mappings'], productMappings);

      await Promise.all([
        refreshData(),
        queryClient.invalidateQueries({
          queryKey: ['products'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['product'],
          exact: false,
        }),
      ]);
    },
  });

  const isSyncingAll = syncAllMutation.isPending || isAutoSyncing;
  const isRefreshingCustomers = refreshCustomersMutation.isPending;
  const isAnySyncing = isSyncingAll || isRefreshingCustomers;
  const justConnected = searchParams.get('blume') === 'connected';

  useEffect(() => {
    if (!justConnected || autoSyncTriggeredRef.current) {
      return;
    }

    autoSyncTriggeredRef.current = true;
    setAutoSyncError(null);
    setIsAutoSyncing(true);

    void (async () => {
      try {
        /*
         * A freshly reconnected integration gets the same full refresh as
         * the header "Sync all" action: customers first, then products.
         */
        await syncBlumeCustomers();
        const productMappings = await getBlumeProductMappings();

        queryClient.setQueryData(['blume-product-mappings'], productMappings);

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ['blume-mappings'],
          }),
          queryClient.invalidateQueries({
            queryKey: ['blume-overview'],
          }),
          queryClient.invalidateQueries({
            queryKey: ['products'],
          }),
          queryClient.invalidateQueries({
            queryKey: ['product'],
            exact: false,
          }),
        ]);
      } catch (syncError) {
        setAutoSyncError(syncError);
      } finally {
        setIsAutoSyncing(false);

        setSearchParams(
          (previous) => {
            const next = new URLSearchParams(previous);
            next.delete('blume');
            return next;
          },
          {
            replace: true,
          }
        );
      }
    })();
  }, [justConnected, queryClient, setSearchParams]);

  const mapMutation = useMutation({
    mutationFn: ({
      blumeCustomerId,
      bmpCustomerId,
    }: {
      blumeCustomerId: string;
      bmpCustomerId: string;
    }) => mapBlumeCustomer(blumeCustomerId, bmpCustomerId),

    onSuccess: async () => {
      setModal(null);
      await refreshData();
    },
  });

  const unmapMutation = useMutation({
    mutationFn: ({
      blumeCustomerId,
      bmpCustomerId,
    }: {
      blumeCustomerId: string;
      bmpCustomerId: string;
    }) => unmapBlumeCustomer(blumeCustomerId, bmpCustomerId),

    onSuccess: async () => {
      await refreshData();
    },
  });

  const ignoreMutation = useMutation({
    mutationFn: ignoreBlumeCustomer,
    onSuccess: async () => {
      await refreshData();
    },
  });

  const unignoreMutation = useMutation({
    mutationFn: unignoreBlumeCustomer,
    onSuccess: async () => {
      await refreshData();
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: disconnectBlume,
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['blume-overview'],
        }),
        queryClient.invalidateQueries({
          queryKey: ['blume-mappings'],
        }),
      ]);

      navigate('/integrations', {
        replace: true,
      });
    },
  });

  const mappings = useMemo(() => {
    if (!data) {
      return [];
    }

    const query = search.trim().toLowerCase();

    return data.mappings.filter((mapping: BlumeMapping) => {
      const mappedCustomerMatches = mapping.bmpCustomers.some(
        (customer) =>
          customer.name?.toLowerCase().includes(query) ||
          customer.email.toLowerCase().includes(query)
      );

      const matchesSearch =
        !query ||
        mapping.blumeName.toLowerCase().includes(query) ||
        mappedCustomerMatches ||
        mapping.suggestedBmpCustomer?.name?.toLowerCase().includes(query) ||
        mapping.suggestedBmpCustomer?.email?.toLowerCase().includes(query);

      if (!matchesSearch) {
        return false;
      }

      switch (filter) {
        case 'mapped':
          return mapping.bmpCustomers.length > 0 && mapping.isPresentInBlume;

        case 'review':
          return (
            mapping.bmpCustomers.length === 0 &&
            mapping.mappingStatus === 'UNMAPPED' &&
            !!mapping.suggestedBmpCustomer &&
            mapping.isPresentInBlume
          );

        case 'blume-only':
          return (
            mapping.bmpCustomers.length === 0 &&
            mapping.mappingStatus === 'UNMAPPED' &&
            !mapping.suggestedBmpCustomer &&
            mapping.isPresentInBlume
          );

        case 'ignored':
          return mapping.mappingStatus === 'IGNORED';

        case 'stale':
          return !mapping.isPresentInBlume;

        case 'bmp-only':
          return false;

        default:
          return true;
      }
    });
  }, [data, filter, search]);

  const bmpOnlyCustomers = useMemo(() => {
    if (!data || filter !== 'bmp-only') {
      return [];
    }

    const query = search.trim().toLowerCase();

    return data.bmpOnlyCustomers.filter(
      (customer: BmpCustomerSummary) =>
        !query ||
        customer.name?.toLowerCase().includes(query) ||
        customer.email.toLowerCase().includes(query)
    );
  }, [data, filter, search]);

  const syncAllError = syncAllMutation.error ?? autoSyncError;
  const customerRefreshError = refreshCustomersMutation.error;

  if (isLoading) {
    return (
      <div className="blume-page blume-page--centered">
        <RefreshCw className="spin" size={24} />
        Loading Blume integration…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="blume-page">
        <button type="button" className="blume-back" onClick={() => navigate('/integrations')}>
          <ArrowLeft size={16} />
          Integrations
        </button>

        <div className="blume-error-panel">
          Unable to load Blume integration.
          {error && <small>{getApiErrorMessage(error, 'Unable to load Blume integration.')}</small>}
        </div>
      </div>
    );
  }

  const counts = data.counts;

  return (
    <div className="layout">
      <AppSidebar />

      <div className="blume-page">
        <button type="button" className="blume-back" onClick={() => navigate('/integrations')}>
          <ArrowLeft size={16} />
          Integrations
        </button>

        <header className="blume-header">
          <div>
            <div className="blume-header__title">
              <h1>Blume</h1>

              <span className="blume-connected">
                <CheckCircle2 size={15} />
                Connected
              </span>
            </div>

            <p>
              Manage customer credit mappings and QuickBooks product/service mappings through Blume.
            </p>

            <div className="blume-last-sync">
              <Clock3 size={14} />
              Last customer sync: {formatDate(data.integration.lastSyncAt)}
            </div>
          </div>

          <div className="blume-header__actions">
            <button
              type="button"
              className="blume-btn blume-btn--secondary"
              disabled={isAnySyncing || disconnectMutation.isPending}
              onClick={() => {
                const confirmed = window.confirm(
                  'Disconnect Blume? Customer syncing and QuickBooks product access through Blume will stop. Existing customer values and saved product mappings will remain in BMP.'
                );

                if (confirmed) {
                  disconnectMutation.mutate();
                }
              }}
            >
              {disconnectMutation.isPending ? 'Disconnecting…' : 'Disconnect Blume'}
            </button>

            <button
              type="button"
              className="blume-btn blume-btn--primary"
              disabled={isAnySyncing || disconnectMutation.isPending}
              onClick={() => syncAllMutation.mutate()}
            >
              <RefreshCw size={16} className={isSyncingAll ? 'spin' : ''} />
              {isSyncingAll ? 'Syncing all…' : 'Sync all'}
            </button>
          </div>
        </header>

        {syncAllError != null && (
          <div className="blume-alert blume-alert--error">
            {getApiErrorMessage(syncAllError, 'Unable to sync all Blume data.')}
          </div>
        )}

        <div className="blume-view-tabs" role="tablist" aria-label="Blume integration data">
          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'customers'}
            className={activeView === 'customers' ? 'active' : ''}
            onClick={() => setActiveView('customers')}
          >
            Customers
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'products'}
            className={activeView === 'products' ? 'active' : ''}
            onClick={() => setActiveView('products')}
          >
            Products & services
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeView === 'invoice-settings'}
            className={activeView === 'invoice-settings' ? 'active' : ''}
            onClick={() => setActiveView('invoice-settings')}
          >
            Invoice settings
          </button>
        </div>

        {activeView === 'customers' ? (
          <>
            <div className="blume-product-intro">
              <div>
                <h2>Blume customers</h2>
                <p>Refresh customer credit, balances, and mapping candidates from Blume.</p>
              </div>

              <button
                type="button"
                className="blume-btn blume-btn--secondary"
                disabled={isAnySyncing || disconnectMutation.isPending}
                onClick={() => refreshCustomersMutation.mutate()}
              >
                <RefreshCw size={16} className={isRefreshingCustomers ? 'spin' : ''} />
                {isRefreshingCustomers ? 'Refreshing…' : 'Refresh customers'}
              </button>
            </div>

            {data.integration.lastSyncError && (
              <div className="blume-alert blume-alert--error">{data.integration.lastSyncError}</div>
            )}

            {customerRefreshError && (
              <div className="blume-alert blume-alert--error">
                {getApiErrorMessage(customerRefreshError, 'Unable to refresh Blume customers.')}
              </div>
            )}

            {mapMutation.error && !modal && (
              <div className="blume-alert blume-alert--error">
                {getApiErrorMessage(mapMutation.error, 'Unable to map the customer.')}
              </div>
            )}

            {unmapMutation.error && (
              <div className="blume-alert blume-alert--error">
                {getApiErrorMessage(unmapMutation.error, 'Unable to remove the customer mapping.')}
              </div>
            )}

            {ignoreMutation.error && (
              <div className="blume-alert blume-alert--error">
                {getApiErrorMessage(ignoreMutation.error, 'Unable to ignore the Blume customer.')}
              </div>
            )}

            {unignoreMutation.error && (
              <div className="blume-alert blume-alert--error">
                {getApiErrorMessage(
                  unignoreMutation.error,
                  'Unable to restore the Blume customer.'
                )}
              </div>
            )}

            <div className="blume-stat-grid">
              <button
                type="button"
                className={`blume-stat ${filter === 'mapped' ? 'active' : ''}`}
                onClick={() => setFilter('mapped')}
              >
                <strong>{counts.mapped}</strong>
                <span>Mapped</span>
              </button>

              <button
                type="button"
                className={`blume-stat ${filter === 'review' ? 'active' : ''}`}
                onClick={() => setFilter('review')}
              >
                <strong>{counts.needsReview}</strong>
                <span>Need review</span>
              </button>

              <button
                type="button"
                className={`blume-stat ${filter === 'blume-only' ? 'active' : ''}`}
                onClick={() => setFilter('blume-only')}
              >
                <strong>{counts.blumeOnly}</strong>
                <span>Blume only</span>
              </button>

              <button
                type="button"
                className={`blume-stat ${filter === 'bmp-only' ? 'active' : ''}`}
                onClick={() => setFilter('bmp-only')}
              >
                <strong>{counts.bmpOnly}</strong>
                <span>BMP only</span>
              </button>
            </div>

            <div className="blume-toolbar">
              <div className="blume-tabs">
                {(
                  [
                    ['all', 'All'],
                    ['mapped', 'Matched'],
                    ['review', 'Needs review'],
                    ['blume-only', 'Blume only'],
                    ['bmp-only', 'BMP only'],
                    ['ignored', 'Ignored'],
                    ['stale', 'Stale'],
                  ] as Array<[FilterType, string]>
                ).map(([value, label]) => (
                  <button
                    type="button"
                    key={value}
                    className={filter === value ? 'active' : ''}
                    onClick={() => setFilter(value)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="blume-search">
                <Search size={16} />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search customers…"
                />
              </div>
            </div>

            {filter !== 'bmp-only' ? (
              <div className="blume-table-wrap">
                <table className="blume-table">
                  <thead>
                    <tr>
                      <th>Blume customer</th>
                      <th>Mapped BMP customers</th>
                      <th>Credit limit</th>
                      <th>Available credit</th>
                      <th>Overdue balance</th>
                      <th>Status</th>
                      <th />
                    </tr>
                  </thead>

                  <tbody>
                    {mappings.map((mapping: BlumeMapping) => {
                      const tone = getMappingTone(mapping);
                      const hasMappings = mapping.bmpCustomers.length > 0;
                      const canAddCustomer =
                        mapping.isPresentInBlume &&
                        mapping.mappingStatus !== 'IGNORED' &&
                        data.bmpOnlyCustomers.length > 0;

                      return (
                        <tr key={mapping.id}>
                          <td>
                            <strong>{mapping.blumeName}</strong>

                            {!mapping.isPresentInBlume && (
                              <small>
                                Last seen {new Date(mapping.lastSeenAt).toLocaleDateString()}
                              </small>
                            )}
                          </td>

                          <td>
                            {hasMappings ? (
                              <div className="mapped-customer-list">
                                {mapping.bmpCustomers.map((customer) => (
                                  <div className="mapped-customer-card" key={customer.mappingId}>
                                    <Link2 size={14} />

                                    <div className="mapped-customer__content">
                                      <strong>{customer.name || customer.email}</strong>
                                      <small>{customer.email}</small>
                                    </div>

                                    <button
                                      type="button"
                                      className="mapped-customer__remove"
                                      disabled={unmapMutation.isPending}
                                      title={`Remove ${customer.name || customer.email}`}
                                      aria-label={`Remove mapping to ${customer.name || customer.email}`}
                                      onClick={() => {
                                        const confirmed = window.confirm(
                                          `Remove "${customer.name || customer.email}" from "${mapping.blumeName}"? The Blume balances will be cleared only from this BMP customer.`
                                        );

                                        if (confirmed) {
                                          unmapMutation.mutate({
                                            blumeCustomerId: mapping.blumeCustomerId,
                                            bmpCustomerId: customer.id,
                                          });
                                        }
                                      }}
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                ))}

                                {mapping.isPresentInBlume &&
                                  mapping.mappingStatus !== 'IGNORED' && (
                                    <button
                                      type="button"
                                      className="mapped-customer-add"
                                      disabled={!canAddCustomer || mapMutation.isPending}
                                      onClick={() => setModal({ mapping })}
                                    >
                                      <Plus size={13} />
                                      Add BMP customer
                                    </button>
                                  )}
                              </div>
                            ) : mapping.suggestedBmpCustomer ? (
                              <div className="suggested-customer">
                                <span>Suggested</span>
                                <strong>
                                  {mapping.suggestedBmpCustomer.name ||
                                    mapping.suggestedBmpCustomer.email}
                                </strong>
                                <small>{mapping.suggestedBmpCustomer.email}</small>
                                {mapping.matchScore !== null && (
                                  <small>{Math.round(mapping.matchScore * 100)}% similarity</small>
                                )}
                              </div>
                            ) : (
                              <span className="empty-value">Not mapped</span>
                            )}
                          </td>
                          <td className="credit-cell">{formatCurrency(mapping.creditLimit)}</td>

                          <td className="credit-cell">{formatCurrency(mapping.creditAvailable)}</td>

                          <td className="credit-cell">
                            {formatCurrency(mapping.overdueInvoiceBalance)}
                          </td>

                          <td>
                            <span className={`mapping-status mapping-status--${tone}`}>
                              {getMappingLabel(mapping)}
                            </span>
                            {hasMappings && (
                              <small className="mapping-count">
                                {mapping.bmpCustomers.length}{' '}
                                {mapping.bmpCustomers.length === 1
                                  ? 'BMP customer'
                                  : 'BMP customers'}
                              </small>
                            )}
                          </td>

                          <td className="mapping-actions">
                            {mapping.mappingStatus === 'IGNORED' ? (
                              <button
                                type="button"
                                className="blume-btn blume-btn--secondary blume-btn--small"
                                disabled={unignoreMutation.isPending}
                                onClick={() => unignoreMutation.mutate(mapping.blumeCustomerId)}
                              >
                                Undo ignore
                              </button>
                            ) : hasMappings ? (
                              <button
                                type="button"
                                className="blume-btn blume-btn--secondary blume-btn--small"
                                disabled={!canAddCustomer || mapMutation.isPending}
                                onClick={() => setModal({ mapping })}
                                title={
                                  data.bmpOnlyCustomers.length === 0
                                    ? 'Every active BMP customer is already mapped'
                                    : undefined
                                }
                              >
                                <Plus size={14} />
                                Add customer
                              </button>
                            ) : (
                              <div className="mapping-actions__group">
                                {mapping.suggestedBmpCustomer && (
                                  <button
                                    type="button"
                                    className="blume-btn blume-btn--primary blume-btn--small"
                                    disabled={mapMutation.isPending || !mapping.isPresentInBlume}
                                    onClick={() =>
                                      mapMutation.mutate({
                                        blumeCustomerId: mapping.blumeCustomerId,
                                        bmpCustomerId: mapping.suggestedBmpCustomer!.id,
                                      })
                                    }
                                  >
                                    <Check size={14} />
                                    Accept
                                  </button>
                                )}

                                <button
                                  type="button"
                                  className="blume-btn blume-btn--secondary blume-btn--small"
                                  disabled={
                                    !mapping.isPresentInBlume ||
                                    mapMutation.isPending ||
                                    data.bmpOnlyCustomers.length === 0
                                  }
                                  onClick={() => setModal({ mapping })}
                                >
                                  <Plus size={14} />
                                  Choose customer
                                </button>

                                <button
                                  type="button"
                                  className="blume-btn blume-btn--ghost blume-btn--small"
                                  disabled={!mapping.isPresentInBlume || ignoreMutation.isPending}
                                  onClick={() => ignoreMutation.mutate(mapping.blumeCustomerId)}
                                >
                                  Ignore
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                    {!mappings.length && (
                      <tr>
                        <td colSpan={7} className="blume-empty">
                          No customers match this filter.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="blume-table-wrap">
                <table className="blume-table">
                  <thead>
                    <tr>
                      <th>BMP customer</th>
                      <th>Email</th>
                      <th>Status</th>
                    </tr>
                  </thead>

                  <tbody>
                    {bmpOnlyCustomers.map((customer: BmpCustomerSummary) => (
                      <tr key={customer.id}>
                        <td>
                          <strong>{customer.name || 'Unnamed customer'}</strong>
                        </td>

                        <td>{customer.email}</td>

                        <td>
                          <span className="mapping-status mapping-status--neutral">BMP only</span>
                        </td>
                      </tr>
                    ))}

                    {!bmpOnlyCustomers.length && (
                      <tr>
                        <td colSpan={3} className="blume-empty">
                          No BMP-only customers.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {modal && (
              <CustomerMappingModal
                mapping={modal.mapping}
                customers={data.bmpOnlyCustomers}
                saving={mapMutation.isPending}
                error={
                  mapMutation.error
                    ? getApiErrorMessage(mapMutation.error, 'Unable to map customer.')
                    : null
                }
                onClose={() => setModal(null)}
                onSelect={(customer) =>
                  mapMutation.mutate({
                    blumeCustomerId: modal.mapping.blumeCustomerId,
                    bmpCustomerId: customer.id,
                  })
                }
              />
            )}
          </>
        ) : activeView === 'products' ? (
          <ProductMappingsView />
        ) : (
          <BlumeInvoiceSettingsPanel />
        )}
      </div>
    </div>
  );
}

function CustomerMappingModal({
  mapping,
  customers,
  saving,
  error,
  onClose,
  onSelect,
}: {
  mapping: BlumeMapping;
  customers: BmpCustomerSummary[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSelect: (customer: BmpCustomerSummary) => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return customers.filter(
      (customer: BmpCustomerSummary) =>
        !query ||
        customer.name?.toLowerCase().includes(query) ||
        customer.email.toLowerCase().includes(query)
    );
  }, [customers, search]);

  const addingAnother = mapping.bmpCustomers.length > 0;

  return (
    <div className="mapping-modal-overlay" onMouseDown={onClose}>
      <div className="mapping-modal" onMouseDown={(event) => event.stopPropagation()}>
        <div className="mapping-modal__header">
          <div>
            <p>{addingAnother ? 'Add BMP customer' : 'Map Blume customer'}</p>
            <h2>{mapping.blumeName}</h2>

            <div className="mapping-modal__financial-summary">
              <span>
                Credit limit <strong>{formatCurrency(mapping.creditLimit)}</strong>
              </span>
              <span className="mapping-modal__financial-divider">•</span>
              <span>
                Available credit <strong>{formatCurrency(mapping.creditAvailable)}</strong>
              </span>

              <span className="mapping-modal__financial-divider">•</span>

              <span>
                Overdue balance <strong>{formatCurrency(mapping.overdueInvoiceBalance)}</strong>
              </span>
            </div>
          </div>

          <button
            type="button"
            className="mapping-modal__close"
            onClick={onClose}
            aria-label="Close mapping dialog"
          >
            <X size={20} />
          </button>
        </div>

        {mapping.bmpCustomers.length > 0 && (
          <div className="mapping-modal__existing">
            <span>Currently mapped</span>

            <div className="mapping-modal__existing-list">
              {mapping.bmpCustomers.map((customer) => (
                <div className="mapping-modal__existing-chip" key={customer.mappingId}>
                  <Link2 size={12} />
                  {customer.name || customer.email}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mapping-modal__search">
          <Search size={16} />

          <input
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search available BMP customer…"
          />
        </div>

        <div className="mapping-modal__customers">
          {filtered.map((customer: BmpCustomerSummary) => (
            <button
              type="button"
              key={customer.id}
              disabled={saving}
              onClick={() => onSelect(customer)}
            >
              <div>
                <strong>{customer.name || customer.email}</strong>
                <small>{customer.email}</small>
              </div>

              <Plus size={16} />
            </button>
          ))}

          {!filtered.length && (
            <div className="mapping-modal__empty">
              {customers.length === 0
                ? 'Every active BMP customer is already mapped.'
                : 'No available BMP customers match your search.'}
            </div>
          )}
        </div>

        {error && <div className="mapping-modal__error">{error}</div>}
      </div>
    </div>
  );
}
type ProductFilterType = 'all' | 'mapped' | 'unmapped' | 'unavailable';

function ProductMappingsView() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<ProductFilterType>('all');
  const [search, setSearch] = useState('');
  const [mappingProduct, setMappingProduct] = useState<BlumeBmpProduct | null>(null);
  const [creatingProduct, setCreatingProduct] = useState<BlumeBmpProduct | null>(null);

  const { data, isLoading, isFetching, error } = useQuery<BlumeProductMappingsResponse, Error>({
    queryKey: ['blume-product-mappings'],
    queryFn: getBlumeProductMappings,
  });

  const refreshProductData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['blume-product-mappings'] }),
      queryClient.invalidateQueries({ queryKey: ['products'] }),
      queryClient.invalidateQueries({ queryKey: ['product'], exact: false }),
    ]);
  };

  const mapProductMutation = useMutation({
    mutationFn: ({
      productId,
      quickBooksItemId,
    }: {
      productId: string;
      quickBooksItemId: string;
    }) => mapExistingBlumeProduct(productId, quickBooksItemId),
    onSuccess: async () => {
      setMappingProduct(null);
      await refreshProductData();
    },
  });

  const createProductMutation = useMutation({
    mutationFn: ({
      productId,
      unitPrice,
      taxable,
    }: {
      productId: string;
      unitPrice?: number | null;
      taxable?: boolean | null;
    }) => createAndMapBlumeProduct(productId, { unitPrice, taxable }),
    onSuccess: async () => {
      setCreatingProduct(null);
      await refreshProductData();
    },
  });

  const unmapProductMutation = useMutation({
    mutationFn: (productId: string) => unmapBlumeProduct(productId),
    onSuccess: async () => {
      await refreshProductData();
    },
  });

  const products = useMemo(() => {
    if (!data) {
      return [];
    }

    const query = search.trim().toLowerCase();

    return data.products.filter((product) => {
      const mappedItem = product.mappedQuickBooksItem;
      const mapping = product.blumeMapping;

      const matchesSearch =
        !query ||
        product.name.toLowerCase().includes(query) ||
        product.sku?.toLowerCase().includes(query) ||
        mappedItem?.name.toLowerCase().includes(query) ||
        mappedItem?.sku?.toLowerCase().includes(query) ||
        mapping?.quickBooksItemName?.toLowerCase().includes(query) ||
        mapping?.quickBooksItemId.toLowerCase().includes(query);

      if (!matchesSearch) {
        return false;
      }

      switch (filter) {
        case 'mapped':
          return !!mapping && product.mappingAvailableInQuickBooks !== false;
        case 'unmapped':
          return !mapping;
        case 'unavailable':
          return !!mapping && product.mappingAvailableInQuickBooks === false;
        default:
          return true;
      }
    });
  }, [data, filter, search]);

  if (isLoading) {
    return (
      <div className="blume-section-loading">
        <RefreshCw className="spin" size={20} />
        Loading QuickBooks products…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="blume-error-panel">
        Unable to load QuickBooks product mappings.
        {error && (
          <small>{getApiErrorMessage(error, 'Unable to load QuickBooks product mappings.')}</small>
        )}
      </div>
    );
  }

  const counts = data.counts;
  const mutationError =
    mapProductMutation.error ?? createProductMutation.error ?? unmapProductMutation.error;

  return (
    <>
      <div className="blume-product-intro">
        <div>
          <h2>QuickBooks products & services</h2>
          <p>
            Map BMP products to existing QuickBooks items or create new items through Blume.
            QuickBooks is the source of truth for SKU once a product is mapped.
          </p>
        </div>

        <button
          type="button"
          className="blume-btn blume-btn--secondary"
          disabled={isFetching}
          onClick={() => void refreshProductData()}
        >
          <RefreshCw size={16} className={isFetching ? 'spin' : ''} />
          {isFetching ? 'Refreshing…' : 'Refresh products'}
        </button>
      </div>

      {mutationError && (
        <div className="blume-alert blume-alert--error">
          {getApiErrorMessage(mutationError, 'Unable to update the product mapping.')}
        </div>
      )}

      <div className="blume-stat-grid">
        <button
          type="button"
          className={`blume-stat ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          <strong>{counts.total}</strong>
          <span>Total products</span>
        </button>

        <button
          type="button"
          className={`blume-stat ${filter === 'mapped' ? 'active' : ''}`}
          onClick={() => setFilter('mapped')}
        >
          <strong>{counts.mapped}</strong>
          <span>Mapped</span>
        </button>

        <button
          type="button"
          className={`blume-stat ${filter === 'unmapped' ? 'active' : ''}`}
          onClick={() => setFilter('unmapped')}
        >
          <strong>{counts.unmapped}</strong>
          <span>Unmapped</span>
        </button>

        <button
          type="button"
          className={`blume-stat ${filter === 'unavailable' ? 'active' : ''}`}
          onClick={() => setFilter('unavailable')}
        >
          <strong>{counts.unavailable}</strong>
          <span>QBO unavailable</span>
        </button>
      </div>

      <div className="blume-toolbar">
        <div className="blume-tabs">
          {(
            [
              ['all', 'All'],
              ['mapped', 'Mapped'],
              ['unmapped', 'Unmapped'],
              ['unavailable', 'Unavailable'],
            ] as Array<[ProductFilterType, string]>
          ).map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={filter === value ? 'active' : ''}
              onClick={() => setFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="blume-search">
          <Search size={16} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search BMP or QuickBooks products…"
          />
        </div>
      </div>

      <div className="blume-table-wrap">
        <table className="blume-table blume-product-table">
          <thead>
            <tr>
              <th>BMP product</th>
              <th>BMP SKU</th>
              <th>QuickBooks product/service</th>
              <th>QBO SKU</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>

          <tbody>
            {products.map((product) => {
              const mapping = product.blumeMapping;
              const quickBooksItem = product.mappedQuickBooksItem;
              const unavailable = !!mapping && product.mappingAvailableInQuickBooks === false;
              const mapped = !!mapping && !unavailable;

              return (
                <tr key={product.id}>
                  <td>
                    <div className="blume-product-name">
                      {product.imageUrl && <img src={product.imageUrl} alt="" />}
                      <div>
                        <strong>{product.name}</strong>
                        <small>{product.deliveryType}</small>
                      </div>
                    </div>
                  </td>

                  <td>
                    {product.sku ? (
                      <code className="blume-sku">{product.sku}</code>
                    ) : (
                      <span className="empty-value">—</span>
                    )}
                  </td>

                  <td>
                    {quickBooksItem ? (
                      <div className="blume-qbo-item">
                        <strong>{quickBooksItem.name}</strong>
                        <small>
                          QBO Item ID {quickBooksItem.quickBooksItemId}
                          {quickBooksItem.fullyQualifiedName &&
                            quickBooksItem.fullyQualifiedName !== quickBooksItem.name &&
                            ` · ${quickBooksItem.fullyQualifiedName}`}
                        </small>
                      </div>
                    ) : mapping ? (
                      <div className="blume-qbo-item blume-qbo-item--unavailable">
                        <strong>{mapping.quickBooksItemName || 'Mapped QuickBooks item'}</strong>
                        <small>
                          QBO Item ID {mapping.quickBooksItemId} · unavailable in current catalog
                        </small>
                      </div>
                    ) : (
                      <span className="empty-value">Not mapped</span>
                    )}
                  </td>

                  <td>
                    {quickBooksItem?.sku ? (
                      <code className="blume-sku">{quickBooksItem.sku}</code>
                    ) : (
                      <span className="empty-value">—</span>
                    )}
                  </td>

                  <td>
                    <span
                      className={`mapping-status ${
                        mapped
                          ? 'mapping-status--success'
                          : unavailable
                            ? 'mapping-status--warning'
                            : 'mapping-status--neutral'
                      }`}
                    >
                      {mapped ? 'Mapped' : unavailable ? 'QBO unavailable' : 'Unmapped'}
                    </span>
                  </td>

                  <td className="mapping-actions">
                    {mapping ? (
                      <div className="mapping-actions__group">
                        <button
                          type="button"
                          className="blume-btn blume-btn--secondary blume-btn--small"
                          disabled={mapProductMutation.isPending || unmapProductMutation.isPending}
                          onClick={() => {
                            mapProductMutation.reset();
                            setMappingProduct(product);
                          }}
                        >
                          Change mapping
                        </button>

                        <button
                          type="button"
                          className="blume-btn blume-btn--ghost blume-btn--small"
                          disabled={mapProductMutation.isPending || unmapProductMutation.isPending}
                          onClick={() => {
                            const label =
                              quickBooksItem?.name ??
                              mapping.quickBooksItemName ??
                              'the QuickBooks item';
                            const confirmed = window.confirm(
                              `Remove the mapping between "${product.name}" and "${label}"? The QuickBooks item will not be deleted.`
                            );

                            if (confirmed) {
                              unmapProductMutation.mutate(product.id);
                            }
                          }}
                        >
                          Unmap
                        </button>
                      </div>
                    ) : (
                      <div className="mapping-actions__group">
                        <button
                          type="button"
                          className="blume-btn blume-btn--primary blume-btn--small"
                          disabled={mapProductMutation.isPending || createProductMutation.isPending}
                          onClick={() => {
                            mapProductMutation.reset();
                            setMappingProduct(product);
                          }}
                        >
                          <Link2 size={14} />
                          Map existing
                        </button>

                        <button
                          type="button"
                          className="blume-btn blume-btn--secondary blume-btn--small"
                          disabled={mapProductMutation.isPending || createProductMutation.isPending}
                          onClick={() => {
                            createProductMutation.reset();
                            setCreatingProduct(product);
                          }}
                        >
                          <Plus size={14} />
                          Create in QBO
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}

            {!products.length && (
              <tr>
                <td colSpan={6} className="blume-empty">
                  No products match this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {mappingProduct && (
        <ProductMappingModal
          product={mappingProduct}
          quickBooksItems={data.quickBooksItems}
          saving={mapProductMutation.isPending}
          error={
            mapProductMutation.error
              ? getApiErrorMessage(mapProductMutation.error, 'Unable to map the product.')
              : null
          }
          onClose={() => {
            mapProductMutation.reset();
            setMappingProduct(null);
          }}
          onSelect={(item) =>
            mapProductMutation.mutate({
              productId: mappingProduct.id,
              quickBooksItemId: item.quickBooksItemId,
            })
          }
        />
      )}

      {creatingProduct && (
        <CreateQuickBooksProductModal
          product={creatingProduct}
          saving={createProductMutation.isPending}
          error={
            createProductMutation.error
              ? getApiErrorMessage(
                  createProductMutation.error,
                  'Unable to create the QuickBooks product.'
                )
              : null
          }
          onClose={() => {
            createProductMutation.reset();
            setCreatingProduct(null);
          }}
          onCreate={(input) =>
            createProductMutation.mutate({
              productId: creatingProduct.id,
              ...input,
            })
          }
        />
      )}
    </>
  );
}

function ProductMappingModal({
  product,
  quickBooksItems,
  saving,
  error,
  onClose,
  onSelect,
}: {
  product: BlumeBmpProduct;
  quickBooksItems: BlumeQuickBooksItem[];
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onSelect: (item: BlumeQuickBooksItem) => void;
}) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return quickBooksItems.filter(
      (item) =>
        !query ||
        item.name.toLowerCase().includes(query) ||
        item.sku?.toLowerCase().includes(query) ||
        item.fullyQualifiedName?.toLowerCase().includes(query) ||
        item.quickBooksItemId.toLowerCase().includes(query)
    );
  }, [quickBooksItems, search]);

  return (
    <div className="mapping-modal-overlay" onMouseDown={onClose}>
      <div className="mapping-modal" onMouseDown={(event) => event.stopPropagation()}>
        <div className="mapping-modal__header">
          <div>
            <p>
              {product.blumeMapping ? 'Change QuickBooks mapping' : 'Map existing QuickBooks item'}
            </p>
            <h2>{product.name}</h2>
            <div className="mapping-modal__financial-summary">
              <span>
                BMP SKU <strong>{product.sku || 'Not set'}</strong>
              </span>
            </div>
          </div>

          <button
            type="button"
            className="mapping-modal__close"
            onClick={onClose}
            aria-label="Close product mapping dialog"
          >
            <X size={20} />
          </button>
        </div>

        <div className="product-mapping-source-note">
          QuickBooks is authoritative for SKU. Selecting an item with an SKU will update the BMP
          product SKU.
        </div>

        <div className="mapping-modal__search">
          <Search size={16} />
          <input
            autoFocus
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search QuickBooks products or SKU…"
          />
        </div>

        <div className="mapping-modal__customers product-mapping-list">
          {filtered.map((item) => {
            const current = product.blumeMapping?.quickBooksItemId === item.quickBooksItemId;

            return (
              <button
                type="button"
                key={item.quickBooksItemId}
                disabled={saving || current}
                onClick={() => onSelect(item)}
              >
                <div className="product-mapping-option">
                  <strong>{item.name}</strong>
                  <small>
                    SKU: {item.sku || '—'} · QBO Item ID {item.quickBooksItemId}
                  </small>
                  {item.fullyQualifiedName && item.fullyQualifiedName !== item.name && (
                    <small>{item.fullyQualifiedName}</small>
                  )}
                </div>

                {current ? (
                  <span className="product-current-mapping">Current</span>
                ) : (
                  <Link2 size={16} />
                )}
              </button>
            );
          })}

          {!filtered.length && (
            <div className="mapping-modal__empty">
              {quickBooksItems.length === 0
                ? 'No active QuickBooks products/services are available.'
                : 'No QuickBooks products match your search.'}
            </div>
          )}
        </div>

        {error && <div className="mapping-modal__error">{error}</div>}
      </div>
    </div>
  );
}

function CreateQuickBooksProductModal({
  product,
  saving,
  error,
  onClose,
  onCreate,
}: {
  product: BlumeBmpProduct;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onCreate: (input: { unitPrice?: number | null; taxable?: boolean | null }) => void;
}) {
  const [unitPrice, setUnitPrice] = useState('');
  const [taxable, setTaxable] = useState<'default' | 'yes' | 'no'>('default');
  const [localError, setLocalError] = useState<string | null>(null);

  const handleCreate = () => {
    const normalizedPrice = unitPrice.trim();
    let parsedPrice: number | null = null;

    if (normalizedPrice) {
      parsedPrice = Number(normalizedPrice);

      if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
        setLocalError('Unit price must be a valid non-negative number.');
        return;
      }
    }

    setLocalError(null);

    onCreate({
      unitPrice: parsedPrice,
      taxable: taxable === 'default' ? null : taxable === 'yes',
    });
  };

  return (
    <div className="mapping-modal-overlay" onMouseDown={onClose}>
      <div
        className="mapping-modal product-create-modal"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="mapping-modal__header">
          <div>
            <p>Create QuickBooks product/service</p>
            <h2>{product.name}</h2>
          </div>

          <button
            type="button"
            className="mapping-modal__close"
            onClick={onClose}
            aria-label="Close create QuickBooks product dialog"
          >
            <X size={20} />
          </button>
        </div>

        <div className="product-create-summary">
          <div>
            <span>BMP SKU</span>
            <strong>{product.sku || 'Not set'}</strong>
          </div>
          <div>
            <span>Product type</span>
            <strong>{product.deliveryType}</strong>
          </div>
        </div>

        <div className="product-mapping-source-note">
          BMP sends the current SKU only as the proposed SKU for this new item. After creation,
          QuickBooks becomes authoritative and the SKU returned by QBO is stored back on the BMP
          product.
        </div>

        <div className="product-create-fields">
          <label>
            <span>Unit price</span>
            <input
              type="number"
              min="0"
              step="0.01"
              inputMode="decimal"
              value={unitPrice}
              onChange={(event) => setUnitPrice(event.target.value)}
              placeholder="Optional"
            />
          </label>

          <label>
            <span>Taxable</span>
            <select
              value={taxable}
              onChange={(event) => setTaxable(event.target.value as 'default' | 'yes' | 'no')}
            >
              <option value="default">Use Blume / QuickBooks default</option>
              <option value="yes">Taxable</option>
              <option value="no">Non-taxable</option>
            </select>
          </label>
        </div>

        {(localError || error) && <div className="mapping-modal__error">{localError || error}</div>}

        <div className="product-create-actions">
          <button
            type="button"
            className="blume-btn blume-btn--secondary"
            disabled={saving}
            onClick={onClose}
          >
            Cancel
          </button>

          <button
            type="button"
            className="blume-btn blume-btn--primary"
            disabled={saving}
            onClick={handleCreate}
          >
            {saving ? (
              <>
                <RefreshCw size={15} className="spin" />
                Creating…
              </>
            ) : (
              <>
                <Plus size={15} />
                Create & map
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
